import styles from "../cms-courses/add-courses-modal.module.scss";
import Button from "components/base/button/button";
import ButtonClose from "components/partial/button-close/button-close";
import { createModalContext, ModalComponent } from "components/base/modal-dialog/modal-dialog";
import { AccountApi } from "api/controllers/AccountApi";
import { IOptionsItem } from "helpers/select.helper";
import { IProfileDataModel } from "models/dto/ZoomiLxp/Models/Profile/IProfileDataModel";
import { GroupBase } from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { useState } from "react";
import { defaultDropdownStyles } from "components/base/dropdown-list/data";
import { ReactComponent as RemoveIcon } from "assets/icons/ic_close.svg";
import classNames from "classnames";
import { ISelectionSearchModel } from "models/dto/ZoomiLxp/Models/Selections/ISelectionSearchModel";
import { ISelectionSearchItem } from "models/dto/ZoomiLxp/Models/Selections/ISelectionSearchItem";
import { SelectionAccountsType } from "models/dto/ZoomiLxp/Utilities/Enumerations/SelectionAccountsType";
import { ICompanyRefModel } from "models/dto/ZoomiLxp/Models/CompanyModels/ICompanyRefModel";
import { IRoleModel } from "models/dto/ZoomiLxp/Models/Profile/IRoleModel";
import { IAssignmentAccountSelectionModel } from "models/dto/ZoomiLxp/Models/BulkOperations/AssignmentBulkOperation/IAssignmentAccountSelectionModel";

interface AddUsersModalProps {
	excludedUserIds: number[];
}

export interface IAddUsersModalItem {
	selection: IAssignmentAccountSelectionModel;
	user: IProfileDataModel;
}

const { modalContext, useModalContext } = createModalContext<IAddUsersModalItem[]>();

const selectionTypes = [SelectionAccountsType.AccountId, SelectionAccountsType.Role, SelectionAccountsType.Company];

const initParams: ISelectionSearchModel = {
	take: 20,
	skip: 0,
	filterCriteria: [],
	sortCriteria: [],
	queryString: "",
	types: selectionTypes,
};

const getUserLabel = (user: IProfileDataModel) => {
	const fullName = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
	if (!user.email) return fullName;
	return fullName ? `${fullName} (${user.email})` : user.email;
};

const getSelectionRowId = ({ id, type }: ISelectionSearchItem) => {
	return type === SelectionAccountsType.AccountId ? id : -type * 100000000 - id;
};

const toUser = (selection: ISelectionSearchItem): IProfileDataModel => ({
	id: getSelectionRowId(selection),
	isActive: true,
	firstName: selection.label,
	lastName: "",
	displayName: selection.label,
	email: selection.type === SelectionAccountsType.AccountId ? selection.subtitle : "",
	roles:
		selection.type === SelectionAccountsType.Role
			? ([{ id: selection.id, name: selection.label, displayName: selection.label }] as IRoleModel[])
			: [],
	jobTitle: "",
	company:
		selection.type === SelectionAccountsType.Company
			? ({ id: selection.id, name: selection.label } as ICompanyRefModel)
			: ({} as ICompanyRefModel),
	country: "",
	city: "",
	description: "",
	registringDate: "",
	likesCount: 0,
	isLiked: false,
	isFollowed: false,
	expertises: [],
	groupIds: [],
	message: "",
	permissions: [],
	mfaEnabled: false,
});

const toOption = (selection: ISelectionSearchItem): IOptionsItem & IAddUsersModalItem => ({
	value: getSelectionRowId(selection),
	label: selection.subtitle ? `${selection.label} (${selection.subtitle})` : selection.label,
	selection: {
		selectionType: selection.type,
		value: selection.id,
	},
	user: toUser(selection),
});

const AddUsersModalComponent = ({ excludedUserIds }: AddUsersModalProps) => {
	const { resolve } = useModalContext();
	const [selectedUsers, setSelectedUsers] = useState<IAddUsersModalItem[]>([]);
	const excludedIds = new Set([...excludedUserIds, ...selectedUsers.map(({ user }) => user.id)]);

	const loadOptions: LoadOptions<any, GroupBase<any>, any> = async (search, prevOptions, { skip }) => {
		const params = { ...initParams, skip, queryString: search };
		const response = await AccountApi.searchSelections(params);
		const records = response.data.data.records.filter((selection) => !excludedIds.has(getSelectionRowId(selection)));

		return {
			options: records.map(toOption),
			hasMore: response.data.data.totalFilteredRecords > prevOptions.length + Number(params.take),
			additional: {
				skip: skip + params.take,
			},
		};
	};

	const handleChange = (option: (IOptionsItem & IAddUsersModalItem) | null) => {
		if (!option || selectedUsers.some(({ user }) => user.id === Number(option.value))) return;
		setSelectedUsers((users) => [...users, { selection: option.selection, user: option.user }]);
	};

	const handleRemove = (userId: number) => {
		setSelectedUsers((users) => users.filter(({ user }) => user.id !== userId));
	};

	return (
		<div className={styles.add_courses_modal}>
			<div className={styles.add_courses_modal__header}>
				<h2 className={styles.add_courses_modal__title}>Add Users</h2>
				<ButtonClose onClick={() => resolve()} />
			</div>
			<div className={styles.add_courses_modal__body}>
				<AsyncPaginate
					additional={{ skip: 0 }}
					debounceTimeout={300}
					value={null}
					loadOptions={loadOptions}
					placeholder="Select User"
					styles={defaultDropdownStyles}
					className={styles.add_courses_modal__select}
					onChange={handleChange}
				/>
				<div className={styles.add_courses_modal__chips_block}>
					{selectedUsers.map(({ user }) => (
						<Button
							key={user.id}
							label={getUserLabel(user)}
							onClick={() => handleRemove(user.id)}
							size="small"
							iconPosition="right"
							icon={<RemoveIcon />}
							className={styles.add_courses_modal__remove_btn}
							labelColor="secondaryText"
						/>
					))}
				</div>
			</div>
			<Button
				label="Add"
				onClick={() => resolve(selectedUsers)}
				disabled={!selectedUsers.length}
				className={classNames(styles.add_courses_modal__add_btn, {
					[styles.add_courses_modal__add_btn_disabled]: !selectedUsers.length,
				})}
			/>
		</div>
	);
};

export const AddUsersModal: ModalComponent<AddUsersModalProps, IAddUsersModalItem[]> = {
	component: AddUsersModalComponent,
	modalContext,
};
